import React, { useState } from 'react';
import { Platform } from 'react-native-web';
import { useSelector } from 'react-redux';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor, colors } from '@styleseat/brand';
import SidebarLocation from './DeprecatedSidebarLocation';
import EditableBusinessHours from './BusinessHours/EditableBusinessHours';
import EditableCancellationPolicy from './CancellationPolicy/EditableCancellationPolicy';
import EditableAboutMe from './AboutMe/EditableAboutMe';
import { useIsMobile, useMount } from '../../../hooks';
import StaticMap from './StaticMap';
import { ShareOptions } from './types';
import { PublicProvider } from '../../../api/Providers';
import type { ProviderScheduleDay } from '../../../store/ProviderWorkSchedule.types';
import { sendClick } from './utils';
import analytics from '../../../modules/analytics';
import {
  Button,
  HeaderText,
  Stack,
} from '../../ui';
import SocialLinks from './SocialLinks/SocialLinks';
import { DeprecatedAboutBusinessHours } from './BusinessHours';
import CancellationPolicy from './CancellationPolicy';
import type { RootState } from '../../../store/models';
import OnboardingPhotoUpload from './onboarding/OnboardingPhotoUpload';
import { DeprecatedProfileImageGridContainer } from './ProfileImageGrid';
import { StylieAwardProfileBanner } from '../../provider/stylies/StylieAwardProfileBanner';
import { ClientProviderProfile } from '../../../api/ProfileClientView';
import { ProfileGalleryImage } from '../../../api/Providers/GalleryImages';
import { useIsStylieAwardee } from '../../../modules/stylies/utils';
import { isGoogleBot } from '../../../modules/BrowserInfo';
import { AboutProDepositPolicy, EditableDepositPolicy } from './DepositPolicy';

type DeprecatedSidebarInfoProps = {
  isEditMode?: boolean;
  isOwnProfile?: boolean;
  onEditPersonalInfo?: () => void;
  onEditBusinessInfo?: () => void;
  onEditCancellationPolicy?: () => void;
  onEditDepositPolicy?: () => void;
  onManageAvailability?: () => void;
  profile: ClientProviderProfile | PublicProvider;
  onAddressPress: () => void;
  onMessagePro: () => void;
  onCallPro: () => void;
  onAddImageClick?: () => void;
  showCallButton: boolean;
  shareOptions: ShareOptions[];
  onShare?: (event: { type: ShareOptions }) => void;
  schedule: ProviderScheduleDay[];
  galleryImages: ProfileGalleryImage[];
};

const DeprecatedSidebarInfo: React.FCWithChildren<DeprecatedSidebarInfoProps> = ({
  isEditMode = false,
  isOwnProfile = false,
  onEditBusinessInfo,
  onEditPersonalInfo,
  onEditDepositPolicy,
  profile,
  onAddressPress,
  onMessagePro,
  onCallPro,
  showCallButton,
  schedule,
  onShare,
  shareOptions,
  onManageAvailability,
  onEditCancellationPolicy,
  galleryImages,
  onAddImageClick,
}) => {
  const routeParams = useSelector<RootState, Record<string, any>>(state => state.route.params);
  const [renderStaticMap, setRenderStaticMap] = useState(false);
  const isMobile = useIsMobile();

  /**
   * Checks if we are being crawled by prerender.io which generates
   * unnecessary Google API fees by displaying maps to bots.
   */
  const checkPrerender = () => {
    if (Platform.OS !== 'web') {
      return setRenderStaticMap(true);
    }

    const shouldHide = isGoogleBot();

    if (shouldHide) {
      analytics.track('client_profile_map_hidden');
    }

    return setRenderStaticMap(!shouldHide);
  };

  useMount(() => {
    checkPrerender();
  });

  const {
    is_awardee: isStylieAwardee,
    isLoading: isLoadingStyliesData,
  } = useIsStylieAwardee(profile);

  if (isLoadingStyliesData) {
    return null;
  }

  return (
    <Stack>
      {!isMobile && isEditMode && !galleryImages.length ? (
        <Box paddingX={8} paddingBottom={8}>
          <OnboardingPhotoUpload onEdit={onAddImageClick} />
        </Box>
      ) : null}
      {isMobile ? null : (
        <DeprecatedProfileImageGridContainer
          onAddImageClick={onAddImageClick}
          galleryImages={galleryImages as ProfileGalleryImage[]}
          profile={profile}
          isEditMode={isEditMode}
          isOwnProfile={isOwnProfile}
          clickParams={{
            ...routeParams,
            search_id: routeParams.sid,
          }}
        />
      )}
      {renderStaticMap ? (
        <StaticMap profile={profile} />
      ) : null}
      {!isMobile && isStylieAwardee ? (
        <StylieAwardProfileBanner profile={profile} />
      ) : null}
      <SidebarLocation
        {...profile}
        onAddressPress={onAddressPress}
        onEdit={onEditBusinessInfo}
        isEditMode={isEditMode}
      />
      {isEditMode ? (
        <Box
          borderTopWidth={1}
          borderStyle="solid"
          borderColor={colors.lightGrey}
          paddingY={16}
        >
          <EditableAboutMe
            blurb={profile.blurb}
            onEdit={sendClick('Edit Bio Clicked')(onEditPersonalInfo)}
            expandable
            centered
          />
        </Box>
      ) : null}
      {/* Contact Info */}
      {isEditMode ? null : (
        <Inline
          space={16}
          paddingTop={8}
          paddingBottom={20}
          justifyContent="center"
        >
          <Button
            onClick={onMessagePro}
            appearance="outline-thin"
            style={{
              lineHeight: '30px',
            }}
            testID="messageButton"
          >
            Message
          </Button>
          {showCallButton && (
            <Button
              onClick={onCallPro}
              appearance="outline-thin"
              style={{
                lineHeight: '30px',
              }}
              testID="callButton"
            >
              Call
            </Button>
          )}
        </Inline>
      )}
      <Box
        borderTopWidth={1}
        borderStyle="solid"
        borderColor={colors.lightGrey}
        paddingY={16}
      >
        {isEditMode ? (
          <EditableBusinessHours
            onEdit={onManageAvailability}
            schedule={schedule}
            centered
          />
        ) : (
          <DeprecatedAboutBusinessHours schedule={schedule} centered />
        )}
      </Box>
      {isEditMode ? (
        <Box
          borderTopWidth={1}
          borderStyle="solid"
          borderColor={BrandColor.LightGrey}
          paddingY={16}
        >
          <EditableDepositPolicy
            onEdit={onEditDepositPolicy}
          />
        </Box>
      ) : null}
      {!isEditMode && profile?.deposit_policy_text ? (
        <Box
          borderTopWidth={1}
          borderStyle="solid"
          borderColor={BrandColor.LightGrey}
          paddingY={16}
        >
          <AboutProDepositPolicy
            providerName={profile.name}
            depositPolicyText={profile.deposit_policy_text}
            centered
          />
        </Box>
      ) : null}
      {isEditMode ? (
        <Box
          borderTopWidth={1}
          borderStyle="solid"
          borderColor={colors.lightGrey}
          paddingY={16}
        >
          <EditableCancellationPolicy
            onEdit={onEditCancellationPolicy}
            providerId={profile.id}
          />
        </Box>
      ) : null}
      {!isEditMode && profile?.cancellation_policy_text ? (
        <Box
          borderTopWidth={1}
          borderStyle="solid"
          borderColor={colors.lightGrey}
          paddingY={16}
        >
          <CancellationPolicy
            providerName={profile.name}
            cancellationPolicyText={profile.cancellation_policy_text}
            centered
          />
        </Box>
      ) : null}
      {shareOptions && !isEditMode ? (
        <Stack
          alignItems="center"
          space={4}
          borderTopWidth={1}
          borderStyle="solid"
          borderColor={colors.lightGrey}
          paddingY={16}
        >
          <HeaderText level={4}>Share Profile</HeaderText>
          <SocialLinks
            onShare={onShare}
            shareOptions={shareOptions}
          />
        </Stack>
      ) : null}
    </Stack>
  );
};

export default DeprecatedSidebarInfo;
